import { Error, Info } from "@mui/icons-material";
import Button from "@mui/material/Button";
import { makeStyles } from "components/mui-core";
import React from "react";
import { grayColor, infoColor, warningColor } from "vendor/colors";
import { useAppNotifications } from "./AppNotificationProvider";

const useStyles = makeStyles<{ dismissed?: boolean }>()(
  (_theme, { dismissed }) => ({
    root: {
      padding: "15px 10px 15px 15px",
      display: "flex",
      gap: "15px",
      alignItems: "center",
      width: "min(700px, 50vw)",
      transition: "background-color 0.5s ease-in-out",
      background: "transparent",
      "&:hover": {
        backgroundColor: "#f8f8f8",
      },
    },
    titleSubtitle: {
      display: "flex",
      flexDirection: "column",
      gap: "3px",
      flex: "1",
    },
    title: {
      fontWeight: !dismissed ? "bold" : "normal",
      fontSize: !dismissed ? "16px" : "15px",
      color: !dismissed ? "inherit" : "#444",
    },
    subtitle: {
      fontSize: !dismissed ? "13px" : "12px",
      color: !dismissed ? "inherit" : "#444",
      display: "flex",
      alignItems: "baseline",
      gap: "5px",
      flexWrap: "wrap",
    },
    button: {
      fontSize: "11px",
    },
    empty: {
      justifyContent: "center",
      color: grayColor[3],
    },
  })
);

interface Props {
  id: string;
  title: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  type: "alert" | "info";
}

export const AppNotification = ({ id, title, subtitle, type }: Props) => {
  const { hide, unhide, visibleNotifications } = useAppNotifications();
  const isDismissed = !visibleNotifications.find((n) => n.id === id);
  const { classes: styles } = useStyles({ dismissed: isDismissed });

  return (
    <div className={styles.root}>
      {type === "alert" && (
        <Error
          htmlColor={!isDismissed ? warningColor[0] : "#888"}
          fontSize="large"
        />
      )}
      {type === "info" && (
        <Info
          htmlColor={!isDismissed ? infoColor[0] : "#888"}
          fontSize="large"
        />
      )}

      <div className={styles.titleSubtitle}>
        <span className={styles.title}>{title}</span>
        {!!subtitle && <span className={styles.subtitle}>{subtitle}</span>}
      </div>

      {!isDismissed && (
        <Button
          variant="text"
          className={styles.button}
          onClick={() => hide(id)}>
          Dismiss
        </Button>
      )}

      {isDismissed && (
        <Button
          variant="text"
          className={styles.button}
          onClick={() => unhide(id)}>
          Undo dismiss
        </Button>
      )}
    </div>
  );
};

export const EmptyAppNotification = () => {
  const { classes: styles, cx } = useStyles({});

  return (
    <div className={cx(styles.root, styles.empty)}>
      There are no new notifications
    </div>
  );
};
